/**
 * Public landing page: a plain explanation of pm-skills, the project-management
 * skills for AI agents that came out of Kaibernetic. No product pitch; the hosted
 * service is retired. Links go to the pm-skills repository and the blog.
 *
 * Wording for the skills follows the repository's README and each SKILL.md; the
 * invariants follow PRINCIPLES.md ("The desiderata").
 */
import { Link } from 'react-router-dom';

import { FadeInSection } from '@/components/FadeInSection';
import { SiteLayout } from '@/components/SiteLayout';
import { Button } from '@/components/ui';
import { GITHUB_URL } from '@/config/site';

const SKILL_GROUPS = [
  {
    when: 'At the start of a session',
    skills: [
      {
        name: 'pm-orient',
        does: 'Reads all tracked work and reports what needs you, what is moving, and what is going stale.',
      },
    ],
  },
  {
    when: 'Before changing anything',
    skills: [
      {
        name: 'pm-pickup',
        does: 'Loads a task’s context, claims it, and checks that the scope still makes sense.',
      },
    ],
  },
  {
    when: 'While working',
    skills: [
      {
        name: 'pm-log',
        does: 'Records progress at meaningful boundaries so any later session can resume from the trail.',
      },
      {
        name: 'pm-capture',
        does: 'Files new tasks, spin-offs and blockers as they come up, so they resurface later instead of evaporating.',
      },
    ],
  },
  {
    when: 'At the end',
    skills: [
      {
        name: 'pm-close',
        does: 'Reconciles statuses with reality, flags what needs the user, and names the next step.',
      },
      {
        name: 'close-session',
        does: 'Runs pm-close, then pm-orient, then scores the session against the invariants below.',
      },
    ],
  },
  {
    when: 'For a new or fuzzy project',
    skills: [
      {
        name: 'pm-init',
        does: 'Asks about vision, constraints, risks, assets and trade-offs until a plan can be drafted.',
      },
      {
        name: 'pm-plan',
        does: 'Drafts the plan as an executive summary with expansions, with honest base rates and capacity.',
      },
      {
        name: 'pm-horizons',
        does: 'A reference for what AI can currently do on its own, by domain, used to decide what to automate, defer or invest in.',
      },
    ],
  },
  {
    when: 'Housekeeping',
    skills: [
      {
        name: 'pm-setup',
        does: 'Installs the skills on a machine, then connects and verifies the tracker.',
      },
      {
        name: 'pm-tune',
        does: 'Revises the skills’ own spec when documented practice and real practice diverge.',
      },
      {
        name: 'pm-hud',
        does: 'A pinned, always-on-top window showing the latest orient read.',
      },
    ],
  },
];

const INVARIANTS = [
  'Recorded statuses match reality. Done means verified done; blocked names the blocker.',
  'The check-in surfaces what needs attention now, not a wall of everything.',
  'Work mentioned mid-task comes back into view later.',
  'A session can end abruptly and the next one resumes from the trail, not from memory.',
  'Every report-back names the next concrete step, or confirms the goal is reached.',
  'Tasks are recorded well enough that someone else, human or agent, could pick them up.',
];

const STEPS = [
  {
    step: '1',
    title: 'Install',
    description: 'Paste the pm-skills link into your assistant and ask it to set the skills up.',
  },
  {
    step: '2',
    title: 'Connect',
    description:
      'Point the skills at your tracker. Linear works out of the box; another tracker needs a short adapter doc mapping the operations to its API.',
  },
  {
    step: '3',
    title: 'Work',
    description:
      'Work as usual. The skills run at the boundaries: session start, task pickup, progress, spin-offs, close.',
  },
];

const GitHubButton = ({ label, ariaLabel }: { label: string; ariaLabel?: string }) => (
  <a href={GITHUB_URL} target="_blank" rel="noreferrer" aria-label={ariaLabel}>
    <Button className="px-8 py-3 text-base">{label}</Button>
  </a>
);

export const LandingPage = () => (
  <SiteLayout>
    {/* What it is */}
    <FadeInSection as="section" delay={0} className="px-6 py-16 md:py-20">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-serif text-4xl font-semibold leading-tight text-text-primary md:text-5xl">
          pm-skills
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-text-secondary md:text-xl">
          A set of skills for AI coding agents that makes the agent maintain your project
          tracker as it works: orient at the start of a session, pick up a task with its
          context, log progress, capture new work as it appears, and close with a truthful
          status and a named next step.
        </p>
        <p className="mt-4 text-base leading-relaxed text-text-secondary">
          The skills are plain markdown files. The tracker underneath is a pluggable adapter;
          Linear is the one in use today.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <GitHubButton label="pm-skills on GitHub" />
          <Link to="/blog" aria-label="Read the Kaibernetic blog">
            <Button variant="secondary" className="px-8 py-3 text-base">
              Read the blog
            </Button>
          </Link>
        </div>
      </div>
    </FadeInSection>

    {/* What it does */}
    <FadeInSection as="section" delay={100} className="bg-surface px-6 py-16 md:py-20">
      <div className="mx-auto max-w-3xl">
        <h2 className="font-serif text-2xl font-semibold text-text-primary md:text-3xl">
          What each skill does
        </h2>
        <div className="mt-8 space-y-8">
          {SKILL_GROUPS.map(({ when, skills }) => (
            <div key={when}>
              <h3 className="text-sm font-medium uppercase tracking-wide text-text-tertiary">{when}</h3>
              <dl className="mt-3 space-y-3">
                {skills.map(({ name, does }) => (
                  <div key={name} className="rounded-panel border border-border bg-background p-4">
                    <dt className="font-mono text-sm font-semibold text-text-primary">{name}</dt>
                    <dd className="mt-1 text-sm text-text-secondary">{does}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>
    </FadeInSection>

    {/* What it keeps true */}
    <FadeInSection as="section" delay={100} className="px-6 py-16 md:py-20">
      <div className="mx-auto max-w-3xl">
        <h2 className="font-serif text-2xl font-semibold text-text-primary md:text-3xl">
          What the skills keep true
        </h2>
        <p className="mt-4 text-base text-text-secondary">
          Each skill states the result it must produce rather than the steps to take. The
          results all serve six invariants about the tracker:
        </p>
        <ol className="mt-6 list-decimal space-y-2 pl-6 text-base text-text-secondary">
          {INVARIANTS.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ol>
      </div>
    </FadeInSection>

    {/* How to use it */}
    <FadeInSection as="section" delay={100} className="bg-surface px-6 py-16 md:py-20">
      <div className="mx-auto max-w-4xl">
        <h2 className="text-center font-serif text-2xl font-semibold text-text-primary md:text-3xl">
          How to use it
        </h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {STEPS.map(({ step, title, description }) => (
            <div key={step} className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
                {step}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-text-primary">{title}</h3>
              <p className="mt-2 text-sm text-text-secondary">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </FadeInSection>

    {/* Where it came from */}
    <FadeInSection as="section" delay={100} className="px-6 py-16 md:py-20">
      <div className="mx-auto max-w-3xl">
        <h2 className="font-serif text-2xl font-semibold text-text-primary md:text-3xl">
          Where it came from
        </h2>
        <div
          role="note"
          className="mt-6 rounded-panel border border-border bg-surface-highlight p-5 text-base leading-relaxed text-text-secondary"
        >
          <span className="font-medium text-text-primary">The hosted service has been retired.</span>{' '}
          Kaibernetic was a hosted project-management product for people building with AI
          assistants, 2025 to 2026. The useful part turned out to be the content: how to elicit
          and plan a project, how to judge what AI can currently do, and how to keep a PM system
          organised while the work moves. AI agents and existing trackers got good enough that an
          agent can set all of that up on its own, with no custom backend. So the service was
          retired in September 2026 and the skills were shared instead. The blog stays here.
        </div>
      </div>
    </FadeInSection>
  </SiteLayout>
);